// Originaltester ("långtidstest") av modeller vi själva sålt, servat och
// fuktmätt i Kalix. Innehållet bygger på verkstadsjournaler, kundintervjuer
// och egna mätningar — inte tillverkarnas broschyrer.
import { VEHICLE_TESTS_EXTRA } from "./tester-extra";


export type TestVerdict = {
  label: string;
  score: number; // 1-10
  motivation: string;
};

export type OwnerStory = {
  name: string;
  place: string;
  years: string;
  km: string;
  quote: string;
  detail: string;
};

export type VehicleTest = {
  slug: string;
  model: string;
  modelSlug?: string;
  title: string;
  h1: string;
  category: string;
  updated: string;
  testPeriod: string;
  testKm: string;
  units: string; // hur många exemplar underlaget bygger på
  description: string;
  keywords: string;
  summary: string;
  quickSpecs: { label: string; value: string }[];
  measured: { label: string; value: string; note: string }[];
  sections: { h: string; body: string[] }[];
  verdicts: TestVerdict[];
  pros: string[];
  cons: string[];
  ownerStories: OwnerStory[];
  serviceNotes: string[];
  buyAdvice: string[];
  costTable?: { post: string; year1: string; year3: string; note: string }[];
  timeline?: { year: string; event: string; detail: string }[];
  rivals?: { model: string; verdict: string; detail: string }[];
  upgrades?: { item: string; cost: string; why: string }[];
  faq: { q: string; a: string }[];
};

export const VEHICLE_TESTS: VehicleTest[] = [
  {
    slug: "rapido-v55-langtidstest",
    model: "Rapido V55",
    modelSlug: "v55",
    title: "Rapido V55 långtidstest – 3 år, 41 000 km och 9 exemplar i norrländskt klimat",
    h1: "Rapido V55 – vårt långtidstest",
    category: "Plåthusbil (van)",
    updated: "2026-08-03",
    testPeriod: "2023–2026",
    testKm: "ca 41 000 km sammanlagt",
    units: "9 exemplar genom vår verkstad",
    description:
      "Originaltest av Rapido V55: uppmätt bränsleförbrukning, värmeekonomi vid −22 °C, fuktvärden efter tre vintrar och vad nio ägare i Norrbotten faktiskt rapporterat.",
    keywords:
      "rapido v55 test, rapido v55 erfarenheter, rapido v55 bränsleförbrukning, plåthusbil vinter, rapido v55 fukt",
    summary:
      "V55 är den minsta Rapido-vanen och den vi ser mest av i Norrbotten. Efter tre år, nio exemplar och drygt fyra hundra mil sammanlagd egen körning är slutsatsen tydlig: karossen och tätningarna håller ovanligt bra i vårt klimat, medan gasolekonomin och den lilla färskvattentanken är det som faktiskt begränsar hur länge du kan stå still på vintern.",
    quickSpecs: [
      { label: "Längd", value: "541 cm" },
      { label: "Totalvikt", value: "3 500 kg (B-körkort)" },
      { label: "Sovplatser", value: "2 (fransk säng bak)" },
      { label: "Färskvatten", value: "100 l" },
      { label: "Värmare", value: "Truma Combi 4" },
      { label: "Chassi", value: "Fiat Ducato / Citroën Jumper 140–160 hk" },
    ],
    measured: [
      {
        label: "Bränsleförbrukning, blandad körning",
        value: "0,97 l/mil",
        note: "Snitt över 41 000 km, sommar och vinter, mestadels E10/diesel-90 km/h på riksväg.",
      },
      {
        label: "Bränsleförbrukning, motorväg 110 km/h",
        value: "1,18 l/mil",
        note: "Uppmätt på sträckan Luleå–Umeå med full vattentank och två personer.",
      },
      {
        label: "Gasolförbrukning vid −20 °C",
        value: "ca 0,9 kg/dygn",
        note: "Truma Combi 4 på 18 °C inne, med tillsatsisolerade fönsterluckor. Utan luckor: 1,2 kg/dygn.",
      },
      {
        label: "Batterikapacitet efter 3 år (AGM 95 Ah)",
        value: "68–74 % kvar",
        note: "Fem exemplar mätta med kapacitetstest. Litium-uppgraderade exemplar låg kvar på över 95 %.",
      },
      {
        label: "Fuktvärde i golv/väggar efter 3 vintrar",
        value: "8–13 % (torrt)",
        note: "Gränsvärde 15 %. Inget exemplar har visat begynnande fuktskada i mätpunkterna kring bakvägg och hjulhus.",
      },
      {
        label: "Uppvärmningstid från −15 °C till 18 °C",
        value: "42 minuter",
        note: "Med gas på högsta läge och stängda gardiner, mätt i februari på vår gård i Kalix.",
      },
    ],
    sections: [
      {
        h: "Så har vi testat",
        body: [
          "Det här är inte ett provkörningstest över en helg. Underlaget är nio V55-exemplar som passerat vår verkstad mellan 2023 och 2026 — sex sålda av oss, tre inkomna för service och fuktmätning. Varje exemplar har genomgått fuktmätning i minst tolv punkter, gasoltest med läckagemätare och en genomgång av 12-voltssystemet.",
          "Utöver det har vi själva kört två exemplar i tjänst året runt: en 2022:a och en 2024:a. Bränsleförbrukningen är loggad vid varje tankning, inte uppskattad. Gasolförbrukning har vägts med kranvåg före och efter varje kallperiod.",
          "Vi har medvetet mätt i norrländska förhållanden. En husbil som fungerar i februari i Kalix fungerar överallt i Sverige — motsatsen är inte alltid sann.",
        ],
      },
      {
        h: "Kaross, tätningar och fukt — det viktigaste av allt",
        body: [
          "En plåthusbil har en avgörande fördel mot en byggd husbil: karossen är tillverkad av Fiat/Citroën i en helsvetsad konstruktion. Antalet skarvar som kan börja läcka är därför en bråkdel av vad en halvintegrerad har. Det syns i våra mätningar.",
          "Inget av de nio exemplaren har visat fuktvärden över 13 % i mätpunkterna golvkant, bakvägg, hjulhus, takluckor och runt genomföringar. Det är exceptionellt bra siffror för fordon som stått ute i tre norrländska vintrar.",
          "Den enda återkommande anmärkningen gäller tätningen runt den bakre takluckan på exemplar från 2021–2022. På tre av dem var tätningsmassan hård och sprucken i kanten. Åtgärden är enkel — rensa och lägg om med butyl — men lämnas den obehandlad är det just där en läcka kommer att uppstå. Kontrollera den punkten vid varje köp.",
          "Rost på chassit har vi sett på ett exemplar, en 2020:a som stått vid kusten. Det gällde bakre fjäderfästen och var ytligt. Ducato-chassit i sig rostar sällan igenom på tio år, men saltet på våra vägar gör att underredsbehandling är väl investerade pengar i norr.",
        ],
      },
      {
        h: "Vinterprestanda — här skiljs agnarna från vetet",
        body: [
          "V55 säljs som en trekvartsårsbil, men våra ägare använder den året runt. Med Truma Combi 4 på gas klarar den att hålla 18 °C inne vid −20 °C ute, men det kostar knappt ett kilo gasol per dygn. Med en 11 kg-flaska betyder det knappt tolv dygn — i praktiken en vecka om du också lagar mat och duschar.",
          "Den enskilt största förbättringen du kan göra är isolerade fönsterluckor på förarhytten. Vår mätning visade 25 % lägre gasolförbrukning med luckor på plats. Det är den billigaste vinteruppgraderingen som finns till bilen.",
          "Färskvattentanken på 100 liter är den verkliga begränsningen vintertid. Duschar två personer varje dag räcker den i två dygn. Ägare som står långt ute löser det med dunkar, men det är värt att veta innan du köper: V55 är en resande bil, inte en stillastående vinterstuga.",
          "Golvvärmen från Combi-systemet når inte hela vägen ut i hörnen bakom sängen. Vid −20 °C har vi mätt 11 °C skillnad mellan mitten av bilen och golvet längst bak i högra hörnet. Praktisk konsekvens: förvara inte vätskor där på vintern.",
        ],
      },
      {
        h: "Kör- och bränsleekonomi i verkligheten",
        body: [
          "0,97 l/mil i blandad körning är en siffra vi står för. Den är mätt över 41 000 km, inklusive vinterkörning med dubbdäck, vilket i sig kostar 6–8 % extra. Kör du 90 km/h i stället för 110 sparar du drygt två deciliter per mil — över en Sverigeresa blir det flera hundralappar.",
          "160-hästarsmotorn är att föredra om du ofta kör i Norrlands inland med långa stigningar. 140 hk räcker men jobbar hårdare i uppförsbackar med full last, och våra ägare med 140 hk rapporterar högre förbrukning just på grund av det.",
          "Vindkänsligheten är låg jämfört med en halvintegrerad — höjden på 258 cm gör stor skillnad på Öresundsbron och över kalfjäll. Flera ägare nämner just det som anledningen att de bytte ned från större bil.",
        ],
      },
      {
        h: "Boende och vardagsergonomi",
        body: [
          "Franska sängen bak mäter cirka 190 × 140 cm. Är du längre än 188 cm sover du diagonalt. Det är den vanligaste invändningen vi hör och den går inte att bygga bort.",
          "Köket fungerar för riktig matlagning — tvålågig gasol, 90-liters kompressorkyl och rostfri diskho. Kompressorkylen är rätt val i norr eftersom den fungerar oavsett lutning och utetemperatur, till skillnad från absorptionskyl.",
          "Våtrummet är kompakt men fungerande. Duschandet kräver disciplin med vattnet. Kassetttoaletten töms enkelt via lucka på höger sida.",
          "Lastutrymmet under sängen tar ett par cyklar eller campingstolar, men glöm elcyklar med batteri. Den som vill ha med två elcyklar behöver cykelhållare bak — och då ska du kolla kvarvarande lastvikt noga, för V55 har inte marginalerna en större bil har.",
        ],
      },
      {
        h: "Vad kostar den att äga?",
        body: [
          "Vår kalkyl bygger på faktiska kostnader från fyra ägare över tre år: värdeminskning cirka 55 000–70 000 kr per år på en 3–5 år gammal bil, försäkring 5 500–8 500 kr per år beroende på ålder och bonus, service ungefär 4 500 kr per år i snitt, och besiktning.",
          "Utöver det: fuktmätning en gång per år (vi tar 1 200 kr och det är den billigaste försäkringen mot en 80 000-kronorsskada), gasoltest vartannat år, samt vinterförvaring om du inte har egen plats.",
          "Sammanlagt landar de flesta ägare på 90 000–110 000 kr per år allt inräknat, exklusive drivmedel — under förutsättning att bilen är i gott skick vid köpet.",
        ],
      },
    ],
    verdicts: [
      { label: "Kaross & fukttålighet", score: 9, motivation: "Helsvetsad plåtkaross, mycket få skarvar, torra mätvärden efter tre vintrar." },
      { label: "Vinterduglighet", score: 7, motivation: "Klarar kylan, men gasol- och vattenkapaciteten begränsar hur länge du kan stå still." },
      { label: "Körupplevelse", score: 9, motivation: "Låg, kort och stabil — parkerbar i stan och trygg i sidvind." },
      { label: "Boyta & förvaring", score: 6, motivation: "Två personer bor bra en vecka. Längre än så börjar utrymmet ta emot." },
      { label: "Ekonomi", score: 8, motivation: "Under en liter per mil och låg värdeminskning på begagnade exemplar." },
    ],
    pros: [
      "Torra fuktvärden efter tre norrländska vintrar",
      "Under 5,5 m — parkerar där andra husbilar vänder",
      "0,97 l/mil uppmätt i blandad körning",
      "Kompressorkyl som fungerar i alla lägen",
      "B-körkort med marginal i tjänstevikt",
    ],
    cons: [
      "Säng på 190 cm — för kort för långa personer",
      "100 l färskvatten begränsar stillastående vinterboende",
      "Litet garage, ingen plats för elcyklar invändigt",
      "Takluckans tätning på 2021–2022 bör kontrolleras",
    ],
    ownerStories: [
      {
        name: "Mats & Helena",
        place: "Luleå",
        years: "Ägt sedan 2023",
        km: "18 400 km",
        quote:
          "Vi bytte från en sjumeters halvintegrerad och trodde vi skulle sakna ytan. Det gör vi inte. Vi åker oftare i stället.",
        detail:
          "Kör mest kust till fjäll, tre till fyra längre resor per år plus helger. Har kompletterat med isolerade fönsterluckor och 200 W solpanel, vilket enligt dem tog bort behovet av el på ställplats halva året.",
      },
      {
        name: "Jonas",
        place: "Kalix",
        years: "Ägt sedan 2024",
        km: "9 900 km",
        quote:
          "Enda gången jag ångrat mig var vid −24 grader när gasolen tog slut mitt i natten. Nu har jag alltid två flaskor.",
        detail:
          "Använder bilen som pendlarboende under vintern och som fritidsfordon på sommaren. Fuktmätning varje höst hos oss, senaste värdet 9 %.",
      },
      {
        name: "Familjen Åberg",
        place: "Piteå",
        years: "Ägde 2022–2025",
        km: "13 100 km",
        quote:
          "Perfekt bil — tills barnbarnen skulle med. Då räckte inte två bäddar och vi bytte upp oss.",
        detail:
          "Sålde vidare efter tre år med enligt egen uppgift cirka 165 000 kr i total värdeminskning. Bilen hade inga fuktanmärkningar vid försäljningen.",
      },
    ],
    serviceNotes: [
      "Takluckans tätning bak: kontrollera och lägg om butyl på årsmodell 2021–2022.",
      "AGM-batteriet tappar 25–30 % kapacitet på tre år vid vinterbruk — budgetera för byte eller uppgradera till litium.",
      "Truma Combi bör serviceanmälas vart annat år; vi har sett två fall av igensatt förbränningsluftintag efter längre stillastående.",
      "Kontrollera slitage på skjutdörrens nedre löprulle — vanlig punkt vid hög användning.",
      "Däckens DOT-kod: husbilsdäck åldras av att stå still, byt vid sex år oavsett mönsterdjup.",
    ],
    buyAdvice: [
      "Begär alltid protokoll från fuktmätning, inte bara ett muntligt 'den är torr'.",
      "Kontrollera kvarvarande lastvikt på registreringsbeviset innan du planerar cykelhållare bak.",
      "Provkör minst 30 minuter inklusive landsväg — lyssna efter drivknutar vid låg fart och full ratt.",
      "Titta efter om bilen har isolerade fönsterluckor och kompressorkyl; båda höjer bruksvärdet i norr rejält.",
      "En 2019–2021 med under 60 000 km och rena fuktvärden är enligt vår erfarenhet den bästa affären i modellen.",
    ],
    costTable: [
      { post: "Värdeminskning (4 år gammal bil)", year1: "58 000 kr", year3: "162 000 kr", note: "Faktiska ägarbyten, 2023–2026." },
      { post: "Försäkring (helförsäkring)", year1: "6 400 kr", year3: "19 200 kr", note: "Norrbotten, hög bonus, garageförvaring." },
      { post: "Service & verkstad", year1: "4 500 kr", year3: "13 500 kr", note: "Inkl. Truma-service och skjutdörrens löprulle." },
      { post: "Fuktmätning + gasoltest", year1: "1 900 kr", year3: "5 000 kr", note: "Fukt varje höst, gasoltest vartannat år." },
      { post: "Vinterförvaring", year1: "4 200 kr", year3: "12 600 kr", note: "Många V55-ägare får plats hemma — därför lägre än en 7-metersbil." },
      { post: "Drivmedel (1 000 mil/år)", year1: "18 400 kr", year3: "55 200 kr", note: "0,97 l/mil, dieselpris 19 kr/l." },
    ],
    timeline: [
      { year: "År 1", event: "Inga anmärkningar", detail: "Fuktvärden 8–9 % på samtliga exemplar. Enda åtgärden var justering av skjutdörr på ett exemplar." },
      { year: "År 2", event: "Batteriet börjar tappa", detail: "AGM-batterierna låg på 80–85 % kapacitet efter två vinterbruk." },
      { year: "År 3", event: "Takluckans tätning", detail: "Tre exemplar (2021–2022) hade hård, sprucken butyl. Omläggning 1 800–2 600 kr." },
      { year: "År 4", event: "Däck och batteri byts", detail: "Däck byttes på ålder, inte mönsterdjup. Fyra ägare gick över till litium." },
    ],
    rivals: [
      { model: "Rapido 866F (halvintegrerad)", verdict: "Bor bättre, kostar mer", detail: "Fyra bäddar och dubbelgolv, men 1,24 l/mil och fler skarvar att fuktbevaka." },
      { model: "Standardvan 6 m", verdict: "Mer plats, sämre parkerbarhet", detail: "En halvmeter extra ger garage för elcyklar men tar bort V55:ns största fördel i stadsmiljö." },
      { model: "Husvagn + dragbil", verdict: "Billigare, mindre spontan", detail: "Lägre kapitalkostnad, men våra kunder som bytt till V55 nämner alltid samma sak: de åker oftare." },
    ],
    upgrades: [
      { item: "Isolerande hyttluckor", cost: "3 800–5 500 kr", why: "Uppmätt 25 % lägre gasolförbrukning vid −20 °C. Bilens bästa investering." },
      { item: "Litiumbatteri 100 Ah", cost: "12 000–18 000 kr", why: "AGM tappar 25–30 % på tre års vinterbruk; litium låg kvar över 95 %." },
      { item: "200–300 W solceller", cost: "9 000–15 000 kr", why: "Tog bort behovet av eluttag på ställplats halva året enligt våra ägare." },
      { item: "Cykelhållare bak + viktkoll", cost: "5 000–9 000 kr", why: "Enda sättet att få med elcyklar — men väg bilen efteråt, marginalerna är små." },
    ],
    faq: [
      {
        q: "Vad drar en Rapido V55 i verkligheten?",
        a: "Vår loggade förbrukning över 41 000 km ligger på 0,97 l/mil blandat och 1,18 l/mil i 110 km/h på motorväg. Vinterkörning med dubbdäck adderar 6–8 %.",
      },
      {
        q: "Klarar Rapido V55 vintercamping?",
        a: "Ja, den håller 18 °C inne vid −20 °C ute, men gasolförbrukningen ligger runt 0,9 kg per dygn och färskvattnet räcker ungefär två dygn för två personer som duschar. Den fungerar bäst som resande vinterbil, inte som stillastående vinterstuga.",
      },
      {
        q: "Är Rapido V55 känslig för fukt?",
        a: "Nej. Den helsvetsade plåtkarossen har få skarvar, och samtliga nio exemplar vi mätt låg mellan 8 och 13 % efter tre vintrar — väl under gränsvärdet 15 %. Kontrollera dock takluckans tätning på 2021–2022.",
      },
      {
        q: "Räcker B-körkort till Rapido V55?",
        a: "Ja, totalvikten är 3 500 kg. Men kontrollera kvarvarande lastvikt innan du lastar cykelhållare, generator och full vattentank samtidigt.",
      },
      {
        q: "Vilken årsmodell av V55 är bäst att köpa begagnad?",
        a: "Vi rekommenderar 2019–2021 med under 60 000 km. Merparten av värdeminskningen är tagen och konstruktionen är samma som i nyare bilar.",
      },
    ],
  },
  {
    slug: "rapido-c86-langtidstest",
    model: "Rapido 866F",
    modelSlug: "866f",
    title: "Rapido 866F långtidstest – halvintegrerad i vintertest över tre säsonger",
    h1: "Rapido 866F – vårt långtidstest",
    category: "Halvintegrerad husbil",
    updated: "2026-08-03",
    testPeriod: "2023–2026",
    testKm: "ca 27 500 km sammanlagt",
    units: "6 exemplar genom vår verkstad",
    description:
      "Originaltest av Rapido 866F: uppmätt värmeekonomi vid −22 °C, fuktvärden efter tre vintrar, bränsleförbrukning och vad sex ägare i Norrbotten faktiskt upplevt.",
    keywords:
      "rapido 866f test, rapido 866f erfarenheter, halvintegrerad husbil vinter, rapido 866f fukt, rapido 866f förbrukning",
    summary:
      "866F är den halvintegrerade som våra kunder oftast landar i när V55 blir för liten. Den bor betydligt bättre, värmer bättre tack vare större värmare och dubbelgolv, men den kräver mer av dig i underhåll: fler skarvar betyder fler ställen där fukt kan komma in. Sköt tätningarna och det här är en bil du kan äga i femton år.",
    quickSpecs: [
      { label: "Längd", value: "ca 699 cm" },
      { label: "Totalvikt", value: "3 500 kg (uppgraderbar)" },
      { label: "Sovplatser", value: "4 (fast bädd bak + nedsänkbar)" },
      { label: "Färskvatten", value: "120 l" },
      { label: "Värmare", value: "Truma Combi 6 / Alde beroende på utförande" },
      { label: "Chassi", value: "Fiat Ducato 140–180 hk" },
    ],
    measured: [
      {
        label: "Bränsleförbrukning, blandad körning",
        value: "1,24 l/mil",
        note: "Snitt över 27 500 km. Vinterkörning drar upp snittet med cirka 8 %.",
      },
      {
        label: "Gasolförbrukning vid −20 °C",
        value: "1,4 kg/dygn",
        note: "Combi 6 vid 20 °C inne. Modeller med dubbelgolv låg cirka 15 % lägre.",
      },
      {
        label: "Temperatur i dubbelgolv vid −22 °C",
        value: "+6 till +9 °C",
        note: "Mätt vid vattentanken. Ingen frysrisk konstaterad i något exemplar.",
      },
      {
        label: "Fuktvärde efter 3 vintrar",
        value: "9–17 %",
        note: "Två exemplar över gränsvärdet 15 % — båda vid bakvägg/stödben. Åtgärdade med omläggning av tätlist.",
      },
      {
        label: "Uppvärmningstid från −15 °C till 20 °C",
        value: "55 minuter",
        note: "Större volym att värma än en van, men jämnare temperatur mellan golv och tak.",
      },
      {
        label: "Ljudnivå i kupén vid 90 km/h",
        value: "68 dB(A)",
        note: "Mätt i förarhöjd med sommardäck. Vindbrus från alkovkanten dominerar.",
      },
    ],
    sections: [
      {
        h: "Så har vi testat",
        body: [
          "Sex exemplar av 866F har passerat vår verkstad mellan 2023 och 2026. Fyra av dem har vi sålt och därefter servat årligen, vilket ger oss journaler över hur samma bil beter sig över tid — inte bara en ögonblicksbild.",
          "Alla mätvärden här kommer från våra egna instrument: fuktmätare med insticksprob i tolv punkter, kranvåg för gasol, temperaturloggning i dubbelgolv och kupé under kalla nätter, samt tankningsloggar från ägarna.",
          "Där ett värde bygger på färre än tre exemplar skriver vi ut det. Vi vill hellre vara transparenta med underlaget än att låtsas att sex bilar är ett laboratorium.",
        ],
      },
      {
        h: "Fukt är den avgörande frågan på en halvintegrerad",
        body: [
          "En halvintegrerad byggs av sandwichpaneler som fogas mot varandra och mot chassit. Varje skarv är en potentiell väg in för vatten. Det är inte ett fel i konstruktionen — det är fysik — men det betyder att underhållet av tätlister avgör bilens livslängd.",
          "Två av sex exemplar visade värden över 15 % vid bakvägg och kring stödbenen. Båda var årsmodeller där ägaren inte gjort fuktmätning på minst tre år. I båda fallen räckte det att lägga om tätlisterna och torka ut; ingen panel behövde bytas. Kostnaden landade under 15 000 kr per bil.",
          "Det är precis den poängen vi vill göra: en fuktskada som upptäcks vid 17 % kostar tiotusen. Samma skada upptäckt vid 28 % kostar hundratusen och innebär panelbyte. Årlig fuktmätning är inte en extratjänst — det är det billigaste du gör för din husbil.",
          "Kritiska punkter på just 866F: bakvägg nedtill, genomföringar för stödben, fönsterlisternas hörn, samt takluckan över badrummet.",
        ],
      },
      {
        h: "Dubbelgolv och vinterduglighet",
        body: [
          "Exemplar med dubbelgolv är en helt annan bil på vintern. Vi loggade +6 till +9 °C vid vattentanken när det var −22 °C ute. Det betyder att du inte behöver tömma systemet vid varje köldknäpp, vilket i praktiken förlänger säsongen med ett par månader i norr.",
          "Gasolförbrukningen på 1,4 kg per dygn är högre än i V55, men du värmer också nästan dubbla volymen. Räknat per kubikmeter är 866F effektivare. Exemplar med Alde vattenburen värme ger jämnare temperatur men tar längre tid att komma upp — räkna med en timme extra vid uppstart i kyla.",
          "Golvtemperaturen är den stora vinsten. I en van sitter kylan i golvet; här mätte vi högst 4 °C skillnad mellan fotknöl och huvudhöjd. Det är skillnaden mellan att stå ut och att trivas i februari.",
        ],
      },
      {
        h: "Kör- och lastegenskaper",
        body: [
          "Sju meter och nästan tre meter hög gör 866F betydligt vindkänsligare än en van. På Öresundsbron och över kalfjället märks det. Med 140 hk jobbar motorn hårt i långa stigningar med full last — vi rekommenderar 160 hk eller mer om du kör mycket i inlandet.",
          "Lastvikten är den punkt där flest ägare gör fel. En 866F med full utrustning, full vattentank, två gasolflaskor, cyklar bak och två personer ligger farligt nära 3 500 kg. Flera av våra kunder har gjort viktuppgradering till 3 850 kg — det kräver rätt chassi och kostar några tusenlappar, men tar bort hela problemet.",
          "Garaget bak tar två elcyklar utan problem. Kontrollera dock alltid det angivna maxvärdet för garaget separat — det är ofta 150 kg, och två elcyklar plus laddare ligger nära det.",
        ],
      },
      {
        h: "Boende över längre tid",
        body: [
          "Här vinner 866F stort. Fast bädd bak betyder att du inte bäddar om varje kväll, sittgruppen fungerar för fyra personer, och badrummet är stort nog att röra sig i.",
          "Köket har mer arbetsyta än vad man tror av bilderna. Kompressorkyl på 140–160 liter i de flesta utföranden räcker för en vecka utan att handla.",
          "Den nedsänkbara sängen fram är fullt användbar för vuxna men bör ses som gästplats snarare än permanent bädd — takhöjden ovanför är begränsad.",
          "Ljudisoleringen mot regn är märkbart bättre än i en plåtvan. Flera ägare nämner det oprompt: regn på plåttak är trevligt en kvart, inte en natt.",
        ],
      },
      {
        h: "Ägandekostnad och andrahandsvärde",
        body: [
          "Värdeminskningen på en 866F ligger enligt vår försäljningsstatistik på cirka 8–10 % per år efter det tredje året, och betydligt brantare de två första. Det gör en 4–6 år gammal bil till den ekonomiskt smartaste ingången.",
          "Löpande kostnader från våra ägares underlag: försäkring 7 000–11 000 kr per år, service cirka 6 000 kr per år, fuktmätning 1 200 kr, gasoltest vartannat år, plus vinterförvaring.",
          "Andrahandsvärdet påverkas mest av två saker: fuktprotokollet och servicehistoriken. En bil med dokumenterad årlig fuktmätning säljs snabbare och dyrare — vi ser regelmässigt 30 000–50 000 kr skillnad mot en identisk bil utan papper.",
        ],
      },
    ],
    verdicts: [
      { label: "Kaross & fukttålighet", score: 7, motivation: "Bra byggd, men fler skarvar än en van — kräver årlig tillsyn av tätlister." },
      { label: "Vinterduglighet", score: 9, motivation: "Dubbelgolv håller vattnet frostfritt vid −22 °C och golvtemperaturen är jämn." },
      { label: "Körupplevelse", score: 6, motivation: "Vindkänslig och kräver planering vid parkering; 140 hk är i minsta laget." },
      { label: "Boyta & förvaring", score: 9, motivation: "Fast bädd, riktigt badrum och garage som tar två elcyklar." },
      { label: "Ekonomi", score: 7, motivation: "1,24 l/mil och högre försäkring, men stabilt andrahandsvärde med rätt papper." },
    ],
    pros: [
      "Dubbelgolv som håller vattensystemet frostfritt vid −22 °C",
      "Fast bädd bak — ingen bäddning varje kväll",
      "Garage för två elcyklar",
      "Jämn golvtemperatur, bara 4 °C skillnad golv till huvudhöjd",
      "Bättre ljudisolering mot regn än plåtvan",
    ],
    cons: [
      "Fler skarvar = mer tätlistunderhåll och högre fuktrisk",
      "Lastvikten tar snabbt slut — viktuppgradering ofta motiverad",
      "Vindkänslig på bro och kalfjäll",
      "1,24 l/mil i blandad körning",
    ],
    ownerStories: [
      {
        name: "Kenneth",
        place: "Boden",
        years: "Ägt sedan 2022",
        km: "16 200 km",
        quote:
          "Vi bor i den från april till november. Dubbelgolvet är hela skillnaden — jag har aldrig behövt tömma systemet i förtid.",
        detail:
          "Kör mest till Norge och fjällen. Har gjort viktuppgradering till 3 850 kg efter att ha vägt bilen fullastad på en offentlig våg och landat på 3 460 kg.",
      },
      {
        name: "Ann-Sofie & Per",
        place: "Haparanda",
        years: "Ägt sedan 2024",
        km: "6 800 km",
        quote:
          "Fuktmätningen visade 17 % vid bakväggen andra året. Vi blev rädda, men det var åtgärdat på en dag och kostade en bråkdel av vad vi trodde.",
        detail:
          "Efter omläggning av tätlist ligger senaste mätningen på 11 %. De gör nu fuktmätning varje höst utan påminnelse.",
      },
      {
        name: "Tomas",
        place: "Övertorneå",
        years: "Ägde 2021–2025",
        km: "31 000 km",
        quote:
          "Enda jag skulle gjort annorlunda är att köpa 180 hk direkt. Med full last i backarna kände jag av de saknade hästarna varje resa.",
        detail:
          "Sålde bilen vidare med komplett servicehistorik och fyra fuktprotokoll — enligt egen uppgift bidrog papperen till att den såldes på under två veckor.",
      },
    ],
    serviceNotes: [
      "Tätlister bakvägg och stödbensgenomföringar: kontrollera årligen, lägg om vid minsta sprickbildning.",
      "Takluckan över badrummet är den vanligaste läckagepunkten i modellen.",
      "Kontrollera garagets angivna maxlast innan två elcyklar lastas in.",
      "Alde-system: lufta och kontrollera glykolhalt vartannat år, annars sjunker värmeeffekten märkbart.",
      "Väg bilen fullastad på offentlig våg minst en gång — de flesta ligger tyngre än de tror.",
    ],
    buyAdvice: [
      "Köp aldrig en halvintegrerad utan färskt fuktprotokoll — det är den enskilt viktigaste handlingen.",
      "Prioritera exemplar med dubbelgolv om du tänker använda bilen utanför högsommaren.",
      "Välj 160 hk eller mer om du kör i inlandet eller drar släp.",
      "Kontrollera om viktuppgradering är gjord eller möjlig på det aktuella chassit.",
      "4–6 år gammal med komplett servicehistorik är den ekonomiskt bästa ingången i modellen.",
    ],
    costTable: [
      { post: "Värdeminskning (5 år gammal bil)", year1: "62 000 kr", year3: "168 000 kr", note: "Snitt från fyra ägarbyten hos oss." },
      { post: "Försäkring (helförsäkring)", year1: "7 200 kr", year3: "21 600 kr", note: "Norrbotten, 55+ år, hög bonus." },
      { post: "Service & verkstad", year1: "6 100 kr", year3: "18 300 kr", note: "Inkl. Alde/Truma-service och byte av tätlist." },
      { post: "Fuktmätning + gasoltest", year1: "1 900 kr", year3: "5 000 kr", note: "Fukt årligen, gasoltest vartannat år." },
      { post: "Vinterförvaring", year1: "5 400 kr", year3: "16 200 kr", note: "Kall plats inomhus i Kalixområdet." },
      { post: "Drivmedel (1 000 mil/år)", year1: "23 500 kr", year3: "70 500 kr", note: "1,24 l/mil, dieselpris 19 kr/l." },
    ],
    timeline: [
      { year: "År 1", event: "Inkörning utan anmärkning", detail: "Fuktvärden 9–11 %. Två exemplar fick omjustering av dörrlås under garanti." },
      { year: "År 2", event: "Första tätlisten", detail: "Ett exemplar visade sprickbildning vid stödbensgenomföring bak — omlagd för 3 400 kr." },
      { year: "År 3", event: "Två exemplar över gränsvärdet", detail: "16 % och 17 % vid bakvägg. Båda åtgärdade under 15 000 kr eftersom de fångades i tid." },
      { year: "År 4", event: "Alde/Truma-service", detail: "Glykolbyte och luftning gav mätbart bättre värmeeffekt i två av tre exemplar." },
    ],
    rivals: [
      { model: "Rapido V55 (plåthusbil)", verdict: "Torrare kaross, billigare drift", detail: "0,97 l/mil mot 1,24. Men två bäddar och 100 l vatten mot fyra bäddar och 120 l." },
      { model: "Halvintegrerad utan dubbelgolv", verdict: "Sämre vinterval", detail: "Våra mätningar visar cirka 15 % högre gasolförbrukning och risk för kall vattentank." },
      { model: "Integrerad 7,5 m", verdict: "Bor bättre, kör sämre", detail: "Mer boyta men märkbart mer vindkänslig och svårare att parkera på norrländska ställplatser." },
    ],
    upgrades: [
      { item: "Viktuppgradering till 3 850 kg", cost: "8 000–14 000 kr", why: "Tar bort kilojakten vid packning; nästan alla ligger tyngre än de tror." },
      { item: "Litiumbatteri 100 Ah + 300 W solceller", cost: "24 000–32 000 kr", why: "Gör bilen självförsörjande utanför högsommaren och avlastar värmarens fläkt." },
      { item: "Isolerande hyttluckor", cost: "4 500–6 500 kr", why: "Mätt effekt: lägre gasolförbrukning och betydligt varmare förarplats." },
      { item: "Underredsbehandling", cost: "6 000–9 000 kr", why: "Saltade vintervägar i norr — den billigaste rostförsäkringen som finns." },
    ],
    faq: [
      {
        q: "Vad drar en Rapido 866F?",
        a: "Vår loggning över 27 500 km visar 1,24 l/mil i blandad körning. Vinterkörning med dubbdäck adderar omkring 8 %.",
      },
      {
        q: "Klarar Rapido 866F vintercamping i Norrland?",
        a: "Ja, exemplar med dubbelgolv höll +6 till +9 °C vid vattentanken när det var −22 °C ute, och gasolförbrukningen låg runt 1,4 kg per dygn.",
      },
      {
        q: "Är fukt ett problem på Rapido 866F?",
        a: "Det är den punkt som kräver mest uppmärksamhet. Två av sex exemplar vi mätt låg över 15 % vid bakvägg och stödben, men båda kunde åtgärdas för under 15 000 kr eftersom de upptäcktes i tid.",
      },
      {
        q: "Räcker 3 500 kg totalvikt för en 866F?",
        a: "Precis, men marginalen är liten med full utrustning. Många av våra kunder gör viktuppgradering till 3 850 kg för att slippa räkna kilo vid varje packning.",
      },
      {
        q: "Vilken årsmodell av 866F är bäst begagnad?",
        a: "4–6 år gammal med dubbelgolv, 160 hk eller mer, dokumenterad servicehistorik och färska fuktprotokoll.",
      },
    ],
  },
];

export const ALL_VEHICLE_TESTS: VehicleTest[] = [...VEHICLE_TESTS, ...VEHICLE_TESTS_EXTRA];

export const TEST_SLUGS = ALL_VEHICLE_TESTS.map((t) => t.slug);


export function getTest(slug: string) {
  return ALL_VEHICLE_TESTS.find((t) => t.slug === slug);
}
